'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import CategorySection from './CategorySection';

interface AITool {
  id: string;
  name: string;
  description: string;
  image: string;
  alt: string;
  rating: number;
  trending: boolean;
  tags: string[];
}

interface Category {
  id: string;
  name: string;
  icon: string;
  description: string;
  tools: AITool[];
}

const AICategoriesInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const router = useRouter();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories: Category[] = [
  {
    id: 'content-creation',
    name: 'Content Creation & Marketing',
    icon: 'PencilSquareIcon',
    description: 'AI tools for creating engaging content, videos, graphics, and marketing materials',
    tools: [
    {
      id: 'cc1',
      name: 'ContentForge AI',
      description: 'Generate viral social media content, blog posts, and marketing copy in 30+ languages with EU GDPR compliance',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_151d0c3d5-1768467083939.png",
      alt: 'Professional content creator working on laptop with social media marketing graphics and analytics dashboard',
      rating: 4.8,
      trending: true,
      tags: ['Social Media', 'Copywriting', 'SEO']
    },
    {
      id: 'cc2',
      name: 'VideoMaster Pro',
      description: 'Create professional videos with AI avatars, voiceovers in Dutch, German, French, and automated subtitles',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_18d217be4-1767046744156.png",
      alt: 'Modern video production studio with professional camera equipment and editing workstation',
      rating: 4.7,
      trending: true,
      tags: ['Video', 'Avatars', 'Multilingual']
    },
    {
      id: 'cc3',
      name: 'DesignGenius AI',
      description: 'Generate stunning graphics, logos, and brand materials optimized for European market trends',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_15d0b73e0-1764764623950.png",
      alt: 'Creative designer workspace with multiple screens showing logo designs and brand identity materials',
      rating: 4.9,
      trending: true,
      tags: ['Design', 'Branding', 'Graphics']
    },
    {
      id: 'cc4',
      name: 'SocialBoost AI',
      description: 'Schedule and optimize posts for Reddit, X (Twitter), LinkedIn with AI-powered engagement analytics',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f60bf1f2-1767961891177.png",
      alt: 'Social media management dashboard displaying engagement metrics and scheduled posts across multiple platforms',
      rating: 4.6,
      trending: false,
      tags: ['Social Media', 'Analytics', 'Scheduling']
    }]

  },
  {
    id: 'crypto-trading',
    name: 'Crypto Trading & Analysis',
    icon: 'CurrencyDollarIcon',
    description: 'Advanced AI tools for cryptocurrency trading, market analysis, and portfolio management',
    tools: [
    {
      id: 'ct1',
      name: 'CryptoSignal Pro',
      description: 'Real-time trading signals for BTC, ETH, BNB with AI-powered market predictions and risk analysis',
      image: "https://images.unsplash.com/photo-1660050913083-5514bb379e78",
      alt: 'Professional cryptocurrency trading terminal with real-time Bitcoin and Ethereum charts and market indicators',
      rating: 4.9,
      trending: true,
      tags: ['Trading', 'Signals', 'BTC/ETH']
    },
    {
      id: 'ct2',
      name: 'Portfolio AI Manager',
      description: 'Automated portfolio rebalancing and tax optimization for European crypto investors',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e404b7d5-1770229455288.png",
      alt: 'Crypto portfolio management interface showing diversified asset allocation and performance analytics',
      rating: 4.7,
      trending: true,
      tags: ['Portfolio', 'Tax', 'Automation']
    },
    {
      id: 'ct3',
      name: 'DeFi Analytics Hub',
      description: 'Track DeFi yields, liquidity pools, and smart contract risks across multiple chains',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f8df0c9e-1768382632838.png",
      alt: 'DeFi analytics dashboard displaying yield farming opportunities and liquidity pool data across blockchains',
      rating: 4.8,
      trending: false,
      tags: ['DeFi', 'Yields', 'Analytics']
    }]

  },
  {
    id: 'privacy-security',
    name: 'Privacy & Security',
    icon: 'ShieldCheckIcon',
    description: 'AI-powered tools for data protection, privacy compliance, and cybersecurity',
    tools: [
    {
      id: 'ps1',
      name: 'PrivacyGuard AI',
      description: 'GDPR compliance automation, data anonymization, and privacy impact assessments for EU businesses',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d6d338b1-1767425589185.png",
      alt: 'Cybersecurity professional monitoring data protection systems with GDPR compliance dashboard',
      rating: 4.9,
      trending: true,
      tags: ['GDPR', 'Privacy', 'Compliance']
    },
    {
      id: 'ps2',
      name: 'SecureVault AI',
      description: 'AI-powered password manager with biometric authentication and breach monitoring',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1441b5568-1767338833988.png",
      alt: 'Secure digital vault interface with fingerprint biometric authentication and encrypted password storage',
      rating: 4.8,
      trending: true,
      tags: ['Security', 'Passwords', 'Biometric']
    },
    {
      id: 'ps3',
      name: 'ThreatDetect Pro',
      description: 'Real-time threat detection and automated incident response for small to medium businesses',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f3787661-1764669639800.png",
      alt: 'Network security operations center with real-time threat monitoring and incident response systems',
      rating: 4.7,
      trending: false,
      tags: ['Cybersecurity', 'Threats', 'Monitoring']
    }]

  },
  {
    id: 'automation',
    name: 'Business Automation',
    icon: 'CogIcon',
    description: 'Intelligent automation tools for workflows, customer service, and operations',
    tools: [
    {
      id: 'ba1',
      name: 'WorkflowAI Master',
      description: 'Automate repetitive tasks, email responses, and data entry with no-code AI workflows',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a7fb1a4d-1768481086272.png",
      alt: 'Business automation dashboard showing workflow diagrams and task automation processes',
      rating: 4.8,
      trending: true,
      tags: ['Automation', 'Workflows', 'No-Code']
    },
    {
      id: 'ba2',
      name: 'ChatBot Builder Pro',
      description: 'Create multilingual AI chatbots for customer support on websites, WhatsApp, and Telegram',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b422d2af-1769707587491.png",
      alt: 'AI chatbot interface with conversation flow builder and multilingual customer support features',
      rating: 4.7,
      trending: true,
      tags: ['Chatbots', 'Support', 'Multilingual']
    },
    {
      id: 'ba3',
      name: 'InvoiceAI Manager',
      description: 'Automated invoice processing, expense tracking, and financial reporting for EU tax compliance',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dd799d05-1768319736753.png",
      alt: 'Financial management system showing automated invoice processing and expense tracking dashboard',
      rating: 4.6,
      trending: false,
      tags: ['Finance', 'Invoices', 'Compliance']
    }]

  },
  {
    id: 'data-analytics',
    name: 'Data & Analytics',
    icon: 'ChartBarIcon',
    description: 'AI-powered data analysis, visualization, and business intelligence tools',
    tools: [
    {
      id: 'da1',
      name: 'DataInsight AI',
      description: 'Transform raw data into actionable insights with natural language queries and automated reports',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_143a21815-1764651949320.png",
      alt: 'Business intelligence dashboard with interactive data visualizations and analytics charts',
      rating: 4.9,
      trending: true,
      tags: ['Analytics', 'BI', 'Reports']
    },
    {
      id: 'da2',
      name: 'PredictiveAI Engine',
      description: 'Forecast sales, customer behavior, and market trends with machine learning models',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1785fdc35-1764668941601.png",
      alt: 'Predictive analytics interface showing sales forecasting models and trend prediction graphs',
      rating: 4.8,
      trending: true,
      tags: ['Forecasting', 'ML', 'Predictions']
    },
    {
      id: 'da3',
      name: 'SurveyAI Analyzer',
      description: 'Analyze customer feedback, Reddit discussions, and X posts with sentiment analysis',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_13f84dfad-1767965143310.png",
      alt: 'Sentiment analysis dashboard showing customer feedback insights and social media monitoring',
      rating: 4.7,
      trending: false,
      tags: ['Sentiment', 'Feedback', 'Social']
    }]

  },
  {
    id: 'whale-toolkit',
    name: 'Whale Toolkit (Elit Araç Paketi)',
    icon: 'SparklesIcon',
    description: 'Elite AI tools for advanced users, crypto whales, and professional operators',
    tools: [
    {
      id: 'wt1',
      name: 'Whale Analytics Pro',
      description: 'Track large crypto transactions, whale movements, and institutional trading patterns across major blockchains',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f0dd18c7-1766522044760.png",
      alt: 'Professional crypto whale tracking dashboard showing large transaction monitoring and institutional trading patterns',
      rating: 4.9,
      trending: true,
      tags: ['Whale Tracking', 'Analytics', 'Institutional']
    },
    {
      id: 'wt2',
      name: 'Elite Portfolio Manager',
      description: 'Advanced portfolio management with AI-powered risk assessment, multi-chain tracking, and tax optimization',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_19f434844-1766495160949.png",
      alt: 'Elite cryptocurrency portfolio management interface with advanced risk metrics and multi-chain asset tracking',
      rating: 4.8,
      trending: true,
      tags: ['Portfolio', 'Risk Management', 'Multi-Chain']
    },
    {
      id: 'wt3',
      name: 'Market Intelligence AI',
      description: 'Real-time market sentiment analysis, social media monitoring, and predictive trend detection for crypto markets',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1beee7a64-1764745256611.png",
      alt: 'Market intelligence dashboard displaying sentiment analysis and predictive trend detection for cryptocurrency markets',
      rating: 4.9,
      trending: true,
      tags: ['Market Intelligence', 'Sentiment', 'Predictions']
    },
    {
      id: 'wt4',
      name: 'Smart Contract Analyzer',
      description: 'Automated smart contract auditing, vulnerability detection, and security scoring for DeFi protocols',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d0185730-1764681470074.png",
      alt: 'Smart contract security analysis interface showing vulnerability detection and automated auditing results',
      rating: 4.7,
      trending: false,
      tags: ['Smart Contracts', 'Security', 'DeFi']
    }]

  },
  {
    id: 'communication',
    name: 'Communication & Collaboration',
    icon: 'ChatBubbleLeftRightIcon',
    description: 'AI tools for team communication, translation, and collaboration',
    tools: [
    {
      id: 'com1',
      name: 'TranslateAI Live',
      description: 'Real-time translation for video calls, chats, and documents in 100+ languages including all EU languages',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b23643b6-1766570889155.png",
      alt: 'Video conference interface with real-time translation overlay showing multilingual communication',
      rating: 4.8,
      trending: true,
      tags: ['Translation', 'Real-time', 'Multilingual']
    },
    {
      id: 'com2',
      name: 'MeetingAI Assistant',
      description: 'Automated meeting transcription, summaries, and action items in Dutch, German, French, English',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1988a976f-1765107830419.png",
      alt: 'Business meeting with AI assistant displaying automated transcription and meeting notes',
      rating: 4.7,
      trending: true,
      tags: ['Meetings', 'Transcription', 'Summaries']
    },
    {
      id: 'com3',
      name: 'EmailAI Writer',
      description: 'Compose professional emails with AI suggestions, tone adjustment, and grammar correction',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b6d7e10c-1768220361868.png",
      alt: 'Professional email composition interface with AI writing suggestions and grammar checking',
      rating: 4.6,
      trending: false,
      tags: ['Email', 'Writing', 'Grammar']
    }]

  },
  {
    id: 'ecommerce',
    name: 'E-commerce & Sales',
    icon: 'ShoppingCartIcon',
    description: 'AI-powered tools for online stores, product optimization, and sales growth',
    tools: [
    {
      id: 'ec1',
      name: 'ProductAI Optimizer',
      description: 'Optimize product listings, pricing, and descriptions for European marketplaces (Amazon, eBay, Bol.com)',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d6ec7e4a-1764660533966.png",
      alt: 'E-commerce dashboard showing product optimization tools and marketplace analytics',
      rating: 4.9,
      trending: true,
      tags: ['E-commerce', 'Optimization', 'Pricing']
    },
    {
      id: 'ec2',
      name: 'RecommendAI Engine',
      description: 'Personalized product recommendations and upselling strategies powered by machine learning',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c4fd5f7e-1769182775824.png",
      alt: 'Online shopping interface with personalized product recommendations and smart upselling features',
      rating: 4.8,
      trending: true,
      tags: ['Recommendations', 'Upselling', 'ML']
    },
    {
      id: 'ec3',
      name: 'InventoryAI Manager',
      description: 'Smart inventory forecasting and stock optimization to reduce costs and prevent stockouts',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_12fe9bac0-1769183759635.png",
      alt: 'Warehouse inventory management system with AI-powered forecasting and stock optimization',
      rating: 4.7,
      trending: false,
      tags: ['Inventory', 'Forecasting', 'Optimization']
    }]

  },
  {
    id: 'education',
    name: 'Education & Learning',
    icon: 'AcademicCapIcon',
    description: 'AI tools for personalized learning, tutoring, and skill development',
    tools: [
    {
      id: 'ed1',
      name: 'LearnAI Tutor',
      description: 'Personalized AI tutor for programming, languages, math, and business skills with adaptive learning',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_174635a62-1766718958576.png",
      alt: 'Online learning platform with AI tutor interface showing personalized lessons and interactive exercises',
      rating: 4.8,
      trending: true,
      tags: ['Education', 'Tutoring', 'Adaptive']
    },
    {
      id: 'ed2',
      name: 'SkillPath AI',
      description: 'Create custom learning paths for career development with AI-curated courses and certifications',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_157e1db29-1769955825498.png",
      alt: 'Career development platform showing personalized learning paths and professional certification programs',
      rating: 4.7,
      trending: true,
      tags: ['Career', 'Learning', 'Certifications']
    },
    {
      id: 'ed3',
      name: 'QuizAI Generator',
      description: 'Generate interactive quizzes, assessments, and study materials from any content',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ef642575-1766839745279.png",
      alt: 'Interactive quiz interface with automated assessment generation and study material creation',
      rating: 4.6,
      trending: false,
      tags: ['Quizzes', 'Assessments', 'Study']
    }]

  }];


  const handleToolClick = () => {
    router.push('/subscriptions');
  };

  const filteredCategories = selectedCategory === 'all' ?
  categories :
  categories.filter((cat) => cat.id === selectedCategory);

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:411:6" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="411" data-component-end-line="417" data-component-file="AICategoriesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pt-24%20pb-12%22%7D" className="min-h-screen bg-background pt-24 pb-12">
        <div data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:412:8" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="412" data-component-end-line="416" data-component-file="AICategoriesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:413:10" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="413" data-component-end-line="415" data-component-file="AICategoriesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%22%7D" className="text-center">
            <p data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:414:12" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="414" data-component-end-line="414" data-component-file="AICategoriesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-muted-foreground%22%2C%22textContent%22%3A%22Loading%20AI%20categories...%22%7D" className="text-lg text-muted-foreground">Loading AI categories...</p>
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:422:4" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="422" data-component-end-line="509" data-component-file="AICategoriesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pt-24%20pb-12%22%7D" className="min-h-screen bg-background pt-24 pb-12">
      <div data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:423:6" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="423" data-component-end-line="508" data-component-file="AICategoriesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:425:8" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="425" data-component-end-line="451" data-component-file="AICategoriesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-16%22%7D" className="text-center mb-16">
          <div data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:426:10" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="426" data-component-end-line="429" data-component-file="AICategoriesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22inline-flex%20items-center%20space-x-2%20bg-secondary%2F10%20px-4%20py-2%20rounded-full%20mb-6%22%7D" className="inline-flex items-center space-x-2 bg-secondary/10 px-4 py-2 rounded-full mb-6">
            <Icon data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:427:12" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="427" data-component-end-line="427" data-component-file="AICategoriesInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22SparklesIcon%22%2C%22className%22%3A%22text-secondary%22%7D" name="SparklesIcon" size={20} className="text-secondary" />
            <span data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:428:12" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="428" data-component-end-line="428" data-component-file="AICategoriesInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-cta%20text-secondary%22%2C%22textContent%22%3A%22Trending%20in%20Europe%22%7D" className="text-sm font-cta text-secondary">Trending in Europe</span>
          </div>
          <h1 data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:430:10" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="430" data-component-end-line="432" data-component-file="AICategoriesInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-4xl%20sm%3Atext-5xl%20lg%3Atext-6xl%20font-headline%20font-bold%20text-foreground%20mb-6%22%2C%22textContent%22%3A%22Premium%20for%20Every%20Sector%22%7D" className="text-4xl sm:text-5xl lg:text-6xl font-headline font-bold text-foreground mb-6">
            Premium <span data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:431:20" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="431" data-component-end-line="431" data-component-file="AICategoriesInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-gradient-cyan%22%2C%22textContent%22%3A%22AI%20Tools%22%7D" className="text-gradient-cyan">AI Tools</span> for Every Sector
          </h1>
          <p data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:433:10" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="433" data-component-end-line="436" data-component-file="AICategoriesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-muted-foreground%20max-w-3xl%20mx-auto%20mb-8%22%2C%22textContent%22%3A%22Discover%20cutting-edge%20AI%20solutions%20trusted%20by%20professionals%20across%20Netherlands%2C%20Germany%2C%20France%2C%20and%20beyond.%20%5Cn%20%20%20%20%20%20%20%20%20%20%20%20All%20tools%20accept%20crypto%20payments%20(BTC%2C%20ETH%2C%20BNB)%20and%20are%20GDPR%20compliant.%22%7D" className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
            Discover cutting-edge AI solutions trusted by professionals across Netherlands, Germany, France, and beyond. 
            All tools accept crypto payments (BTC, ETH, BNB) and are GDPR compliant.
          </p>
          <div data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:437:10" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="437" data-component-end-line="450" data-component-file="AICategoriesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20space-x-6%20text-sm%20text-muted-foreground%22%7D" className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
            <div data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:438:12" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="438" data-component-end-line="441" data-component-file="AICategoriesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%22%7D" className="flex items-center space-x-2">
              <Icon data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:439:14" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="439" data-component-end-line="439" data-component-file="AICategoriesInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckCircleIcon%22%2C%22className%22%3A%22text-success%22%7D" name="CheckCircleIcon" size={20} className="text-success" />
              <span data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:440:14" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="440" data-component-end-line="440" data-component-file="AICategoriesInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Crypto%20Payments%22%7D">Crypto Payments</span>
            </div>
            <div data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:442:12" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="442" data-component-end-line="445" data-component-file="AICategoriesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%22%7D" className="flex items-center space-x-2">
              <Icon data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:443:14" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="443" data-component-end-line="443" data-component-file="AICategoriesInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ShieldCheckIcon%22%2C%22className%22%3A%22text-success%22%7D" name="ShieldCheckIcon" size={20} className="text-success" />
              <span data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:444:14" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="444" data-component-end-line="444" data-component-file="AICategoriesInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22GDPR%20Compliant%22%7D">GDPR Compliant</span>
            </div>
            <div data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:446:12" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="446" data-component-end-line="449" data-component-file="AICategoriesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%22%7D" className="flex items-center space-x-2">
              <Icon data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:447:14" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="447" data-component-end-line="447" data-component-file="AICategoriesInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22GlobeAltIcon%22%2C%22className%22%3A%22text-success%22%7D" name="GlobeAltIcon" size={20} className="text-success" />
              <span data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:448:14" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="448" data-component-end-line="448" data-component-file="AICategoriesInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22EU%20Optimized%22%7D">EU Optimized</span>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:454:8" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="454" data-component-end-line="479" data-component-file="AICategoriesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-12%22%7D" className="mb-12">
          <div data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:455:10" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="455" data-component-end-line="478" data-component-file="AICategoriesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20gap-3%20justify-center%22%7D" className="flex flex-wrap gap-3 justify-center">
            <button data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:456:12" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="456" data-component-end-line="463" data-component-file="AICategoriesInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22All%20Categories%22%7D"
              onClick={() => setSelectedCategory('all')}
              className={`px-6 py-3 rounded-lg font-cta font-semibold text-sm transition-all duration-300 ${
              selectedCategory === 'all' ? 'bg-secondary text-secondary-foreground cosmic-glow' : 'bg-card text-foreground cosmic-border hover:cosmic-border-active'}`
              }>

              All Categories
            </button>
            {categories.map((category) =>
            <button data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:465:12" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="465" data-component-end-line="476" data-component-file="AICategoriesInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-cta font-semibold text-sm transition-all duration-300 ${
              selectedCategory === category.id ?
              'bg-secondary text-secondary-foreground cosmic-glow' :
              'bg-card text-foreground cosmic-border hover:cosmic-border-active'}`
              }>

                <Icon data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:474:16" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="474" data-component-end-line="474" data-component-file="AICategoriesInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={category.icon as any} size={18} />
                <span data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:475:16" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="475" data-component-end-line="475" data-component-file="AICategoriesInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{category.name}</span>
              </button>
            )}
          </div>
        </div>

        {/* Categories Display */}
        <div data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:482:8" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="482" data-component-end-line="490" data-component-file="AICategoriesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-16%22%7D" className="space-y-16">
          {filteredCategories.map((category) =>
          <CategorySection data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:484:10" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="484" data-component-end-line="487" data-component-file="AICategoriesInteractive.tsx" data-component-name="CategorySection" data-component-content="%7B%22elementName%22%3A%22CategorySection%22%7D"
            key={category.id}
            category={category}
            onToolClick={handleToolClick} />

          )}
        </div>

        {/* CTA Section */}
        <div data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:493:8" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="493" data-component-end-line="507" data-component-file="AICategoriesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-20%20text-center%20bg-card%20rounded-2xl%20p-12%20cosmic-border%22%7D" className="mt-20 text-center bg-card rounded-2xl p-12 cosmic-border">
          <h2 data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:494:10" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="494" data-component-end-line="496" data-component-file="AICategoriesInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20font-headline%20font-bold%20text-foreground%20mb-4%22%2C%22textContent%22%3A%22Ready%20to%20Access%20%3F%22%7D" className="text-3xl font-headline font-bold text-foreground mb-4">
            Ready to Access <span data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:495:28" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="495" data-component-end-line="495" data-component-file="AICategoriesInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-gradient-cyan%22%2C%22textContent%22%3A%22Premium%20AI%20Tools%22%7D" className="text-gradient-cyan">Premium AI Tools</span>?
          </h2>
          <p data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:497:10" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="497" data-component-end-line="499" data-component-file="AICategoriesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-muted-foreground%20mb-8%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22Subscribe%20now%20to%20unlock%20all%20AI%20tools%20and%20features.%20Pay%20securely%20with%20crypto%20(BTC%2C%20ETH%2C%20BNB).%22%7D" className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Subscribe now to unlock all AI tools and features. Pay securely with crypto (BTC, ETH, BNB).
          </p>
          <button data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:500:10" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="500" data-component-end-line="506" data-component-file="AICategoriesInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-8%20py-4%20bg-cta%20text-cta-foreground%20rounded-lg%20font-cta%20font-semibold%20text-lg%20hover%3Abg-opacity-90%20transition-all%20duration-300%20cosmic-glow%20hover%3Acosmic-glow-intense%20inline-flex%20items-center%20space-x-2%22%7D"
            onClick={() => router.push('/subscriptions')}
            className="px-8 py-4 bg-cta text-cta-foreground rounded-lg font-cta font-semibold text-lg hover:bg-opacity-90 transition-all duration-300 cosmic-glow hover:cosmic-glow-intense inline-flex items-center space-x-2">

            <span data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:504:12" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="504" data-component-end-line="504" data-component-file="AICategoriesInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22View%20Subscription%20Plans%22%7D">View Subscription Plans</span>
            <Icon data-component-id="src/app/ai-categories/components/AICategoriesInteractive.tsx:505:12" data-component-path="src/app/ai-categories/components/AICategoriesInteractive.tsx" data-component-line="505" data-component-end-line="505" data-component-file="AICategoriesInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowRightIcon%22%7D" name="ArrowRightIcon" size={20} />
          </button>
        </div>
      </div>
    </div>);

};

export default AICategoriesInteractive;